const TARGET_TICKS = 5;

const TICK_PRECISION = 12;

const NICE_FACTORS = [1, 2, 5, 10] as const;

export type ValueScale = { readonly ticks: readonly number[]; readonly bottom: number; readonly top: number };

const niceStepOf = (span: number): number => {
  const rough = span / TARGET_TICKS;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const factor = NICE_FACTORS.find((nice) => rough / magnitude <= nice) ?? 10;
  return factor * magnitude;
};

const preciseOf = (value: number): number => Number(value.toPrecision(TICK_PRECISION)) + 0;

const FLAT_SCALE: ValueScale = { ticks: [0], bottom: 0, top: 0 };

export const scaleOf = (values: readonly number[]): ValueScale => {
  const highest = Math.max(0, ...values);
  const lowest = Math.min(0, ...values);
  if (highest === lowest) return FLAT_SCALE;
  const step = niceStepOf(highest - lowest);
  const bottom = Math.floor(lowest / step) * step;
  const top = Math.ceil(highest / step) * step;
  const ticks = Array.from({ length: Math.round((top - bottom) / step) + 1 }, (_, index) =>
    preciseOf(bottom + index * step),
  );
  return { ticks, bottom: preciseOf(bottom), top: preciseOf(top) };
};

export const shareOnScale = (scale: ValueScale, value: number): number =>
  scale.top === scale.bottom ? 0 : (value - scale.bottom) / (scale.top - scale.bottom);

export const ticksOf = (highest: number): readonly number[] => scaleOf([Math.max(highest, 0)]).ticks;
